import { z } from 'zod';
import { GlobalAPIError, factory } from '@shory-health-individual/customer-api';
import { useAppPlatformContextOptions } from '../contexts/app';

type UseDownloadFile = {
	onSuccess?: () => void;
	isWebView?: boolean;
};

type DownloadFileProps = {
	url: string;
	method: 'POST' | 'PATCH' | 'PUT' | 'GET';
	parameters?: Record<string, string | number>;
	body?: object;
};

declare global {
	interface Window {
		ReactNativeWebView?: {
			postMessage: (message: string) => void;
		};
	}
}

type TParameters = Record<string, ['required' | 'optional', 'string' | 'number' | 'stringArray' | 'numberArray']>;

const useDownloadFile = (props?: UseDownloadFile) => {
	const { apiConnectionOptions, onGeneralErrorHandling } = useAppPlatformContextOptions();

	const getFileNameFromHeader = (responseHeaders: Record<string, unknown>) => {
		const contentDisposition = responseHeaders?.['content-disposition'];
		const filename = (contentDisposition as string)?.match(/filename="?(?<filename>[^,;"]+)"?;/)?.[1];

		return filename;
	};

	const convertBlobToBase64 = (blob: Blob): Promise<string> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();

			reader.onloadend = () => {
				const base64 = reader.result;

				if (typeof base64 === 'string') {
					resolve(base64);
				} else {
					reject('Failed to convert blob to Base64');
				}
			};
			reader.onerror = () => reject('Failed to read the blob');
			reader.readAsDataURL(blob);
		});
	};

	const onDownload = async ({ url, method, parameters = {}, body = {} }: DownloadFileProps) => {
		try {
			const { file, fileName } = await factory(apiConnectionOptions).createAPIDefinition({
				url,
				method,
				requestParameters: parameters as unknown as TParameters,
				requestSchema: z.object({}),
				responseType: 'blob',
				responseSchema: z.object({
					file: z.instanceof(Blob),
					fileName: z.string().optional(),
				}),
				transformResponse: (data, headers) => ({
					file: data,
					fileName: getFileNameFromHeader(headers),
				}),
			})({ parameters, body });

			if (props?.isWebView) {
				const base64Data = await convertBlobToBase64(file);

				if (window.ReactNativeWebView) {
					window.ReactNativeWebView.postMessage(JSON.stringify({ base64Data, fileName }));
				}
			} else {
				const anchor = document.createElement('a');

				anchor.href = URL.createObjectURL(file);
				anchor.setAttribute('download', fileName ?? '');
				anchor.click();
				anchor.remove();
			}

			props?.onSuccess?.();
		} catch (e) {
			onGeneralErrorHandling(e as GlobalAPIError);
		}
	};

	return { onDownload };
};

export default useDownloadFile;
